import { useMemo } from 'react'

import { useDocumentsView } from 'src/features/documents/hooks/useDocumentsView'
import type { UsageView } from 'src/features/documents/types/documents.types'
import { isMatrixGroupView } from 'src/features/documents/types/documents.types'
import { type ProjectFileUsageType } from '@autoriz/shared/files'
import type { ProjectStepType } from '@autoriz/shared/project'
import { useHubProjectDetail } from '@features/projects/hub/hooks/useHubProjectDetail'

import { getDocumentSlotDefinitions } from '../services/documentSlotDefinitions'
import type { DocumentSlotDefinition } from '../types'

// One uploaded file within a slot group. Trimmed projection of the
// documents-view FileView shape — the slot UI resolves a presigned URL
// (thumbnail for images, original for other documents) from the file id.
// `status` carries the architect's review outcome so the slot can flag a
// REJECTED file and exclude it from the "filled" count.
export type SlotFile = {
  id: string
  name: string
  sizeBytes: number
  mimeType: string | null
  status: 'ACCEPTED' | 'REJECTED' | null
  // The architect's per-file review note — shown on a REJECTED tile so the
  // salesman knows why the piece came back. Empty/null when none was left.
  comment: string | null
}

// A file counts toward a slot's requirement only when the architect has NOT
// rejected it — a rejected piece must be re-uploaded, so it must not make the
// slot look satisfied.
export const isCountedSlotFile = (f: SlotFile): boolean =>
  f.status !== 'REJECTED'

// One slot row = one mandatory usage. The salesman uploads up to `required`
// files per group (architect rule, e.g. 6 photos of the house, 1 drawing view).
export type DocumentSlotRow = {
  definition: DocumentSlotDefinition
  files: ReadonlyArray<SlotFile>
  required: number
  isEditable: boolean
}

export type UseProjectHubDocumentSlotsResult = {
  slots: ReadonlyArray<DocumentSlotRow>
  filledCount: number
  totalRequired: number
  isLoading: boolean
}

const findUsageGroup = (
  groups: ReadonlyArray<unknown>,
  usage: ProjectFileUsageType,
): UsageView | undefined => {
  for (const g of groups as ReadonlyArray<UsageView | { usages: unknown }>) {
    if (isMatrixGroupView(g as never)) continue
    const v = g as UsageView
    if (v.key === usage) return v
  }
  return undefined
}

export const useProjectHubDocumentSlots = (
  projectId: number | undefined,
): UseProjectHubDocumentSlotsResult => {
  const { project, isLoading: isProjectLoading } = useHubProjectDetail(projectId)
  const { groups, isLoading: isViewLoading } = useDocumentsView(projectId ?? 0)

  const slots = useMemo<ReadonlyArray<DocumentSlotRow>>(() => {
    if (!project) return []
    const definitions = getDocumentSlotDefinitions({
      step: project.step as ProjectStepType,
      projectType: project.project_type,
    })
    return definitions.map((definition) => {
      const group = findUsageGroup(groups, definition.usage)
      const files: SlotFile[] = group
        ? group.files
            .filter((f) => f.origin === 'project')
            .map((f) => ({
              id: f.id,
              name: f.name,
              sizeBytes: f.size_bytes ?? 0,
              mimeType: f.mime_type ?? null,
              status: f.status,
              comment: f.comment ?? null,
            }))
        : []
      return {
        definition,
        files,
        required: group?.required ?? definition.required,
        isEditable: group?.is_editable ?? false,
      }
    })
  }, [project, groups])

  // Rejected files don't count as filled — they must be re-uploaded.
  const filledCount = slots.reduce(
    (acc, s) => acc + s.files.filter(isCountedSlotFile).length,
    0,
  )
  const totalRequired = slots.reduce((acc, s) => acc + s.required, 0)

  return {
    slots,
    filledCount,
    totalRequired,
    isLoading: isProjectLoading || isViewLoading,
  }
}
